import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid
} from "recharts";
import {
  Sparkles, MessageCircle, Download, ArrowRight, Lock, Send, Brain,
  Trophy, Target, Lightbulb, TrendingUp, Loader2, Gift, Copy, Mail, Phone
} from "lucide-react";
import api, { API } from "../lib/api";
import { toast } from "sonner";
import Nav from "../components/Nav";
import PaymentModal from "../components/PaymentModal";
import { BharatIQScoreCard } from "./Result";
import { track } from "../lib/analytics";

export default function Dashboard() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(params.get("session"));
  const [scores, setScores] = useState(null);
  const [report, setReport] = useState(null);
  const [paid, setPaid] = useState({ basic_unlocked: false, premium_unlocked: false });
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [orders, setOrders] = useState([]);
  const chatBottomRef = useRef(null);

  // Load list of sessions
  useEffect(() => {
    (async () => {
      try {
        const [sR, cR, oR] = await Promise.all([
          api.get("/me/sessions"),
          api.get("/coupons").catch(() => ({ data: [] })),
          api.get("/orders").catch(() => ({ data: [] })),
        ]);
        const completed = sR.data.filter((s) => s.status === "completed");
        setSessions(completed);
        setCoupons(cR.data || []);
        setOrders(oR.data || []);
        if (!activeSessionId && completed.length > 0) {
          setActiveSessionId(completed[0].id);
        }
      } catch (e) { /* */ }
    })();
  }, []); // eslint-disable-line

  // Load session details + poll for report if generating
  useEffect(() => {
    if (!activeSessionId) return;
    let pollTimer = null;
    const fetchAll = async (firstLoad = false) => {
      try {
        const [s, p] = await Promise.all([
          api.get(`/test/session/${activeSessionId}/scores`),
          api.get(`/payments/status/${activeSessionId}`),
        ]);
        if (firstLoad) setScores(s.data);
        setPaid(p.data);
        if (p.data.basic_unlocked || p.data.premium_unlocked) {
          try {
            const r = await api.get(`/reports/${activeSessionId}`);
            setReport(r.data);
            return true; // report ready
          } catch (e) {
            return false;
          }
        } else {
          setReport(null);
          return true;
        }
      } catch (e) {
        if (firstLoad) toast.error("Could not load report");
        return false;
      }
    };

    (async () => {
      const ready = await fetchAll(true);
      if (!ready) {
        // poll every 4s for up to 60s
        let attempts = 0;
        pollTimer = setInterval(async () => {
          attempts += 1;
          const ok = await fetchAll(false);
          if (ok || attempts >= 15) clearInterval(pollTimer);
        }, 4000);
      }
      // load chat (premium)
      if (paid.premium_unlocked) {
        try {
          const m = await api.get(`/chat/${activeSessionId}/messages`);
          setChat(m.data);
        } catch (e) { setChat([]); }
      }
    })();

    return () => { if (pollTimer) clearInterval(pollTimer); };
    // eslint-disable-next-line
  }, [activeSessionId]);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat]);

  if (!activeSessionId) {
    return (
      <div className="min-h-screen bg-background">
        <Nav />
        <div className="max-w-4xl mx-auto px-6 py-16">
          {coupons.length > 0 && (
            <div className="mb-10 no-print" data-testid="coupon-wallet">
              <div className="label-eyebrow mb-3">Your Rewards</div>
              <h2 className="font-display text-2xl font-extrabold tracking-tight flex items-center gap-2 mb-4">
                <Gift className="w-5 h-5 text-accent" /> Coupon Wallet
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {coupons.map((c) => <CouponCard key={c.id} coupon={c} />)}
              </div>
            </div>
          )}
          <div className="text-center py-10">
            <div className="w-16 h-16 mx-auto rounded-full bg-primary/5 grid place-items-center">
              <Brain className="w-8 h-8 text-primary" />
            </div>
            <h1 className="mt-6 font-display text-3xl font-extrabold">No assessments yet</h1>
            <p className="mt-3 text-muted-foreground">Take your first 15-minute assessment to unlock your dashboard.</p>
            <Link to="/test" className="mt-6 inline-flex btn-primary px-5 py-3 rounded-md font-semibold gap-2 items-center">
              Start Assessment <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!scores) {
    return (
      <div className="min-h-screen bg-background">
        <Nav />
        <div className="grid place-items-center py-20"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
      </div>
    );
  }

  const radarData = [
    { trait: "Openness", value: scores.personality.O },
    { trait: "Conscientiousness", value: scores.personality.C },
    { trait: "Extraversion", value: scores.personality.E },
    { trait: "Agreeableness", value: scores.personality.A },
    { trait: "Stability", value: 100 - scores.personality.N },
  ];

  const riasecData = [
    { code: "R", value: scores.riasec.R, label: "Realistic" },
    { code: "I", value: scores.riasec.I, label: "Investigative" },
    { code: "A", value: scores.riasec.A, label: "Artistic" },
    { code: "S", value: scores.riasec.S, label: "Social" },
    { code: "E", value: scores.riasec.E, label: "Enterprising" },
    { code: "C", value: scores.riasec.C, label: "Conventional" },
  ];

  const sendChat = async (e) => {
    e?.preventDefault();
    const text = chatInput.trim();
    if (!text || chatSending) return;
    setChatSending(true);
    const localUserMsg = { role: "user", content: text, id: `temp-${Date.now()}` };
    setChat((c) => [...c, localUserMsg]);
    setChatInput("");
    try {
      const r = await api.post("/chat/send", { session_id: activeSessionId, message: text });
      setChat((c) => [...c.filter((m) => m.id !== localUserMsg.id), r.data.user_message, r.data.assistant_message]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Advisor failed");
      setChat((c) => c.filter((m) => m.id !== localUserMsg.id));
    } finally {
      setChatSending(false);
    }
  };

  const printPDF = async () => {
    if (!activeSessionId || pdfBusy) return;
    setPdfBusy(true);
    track("report_download", { session_id: activeSessionId });
    try {
      const token = localStorage.getItem("bharatiq_token");
      const r = await fetch(`${API}/reports/${activeSessionId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "PDF generation failed");
      }
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `BharatIQ-Report-${activeSessionId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error(e.message || "PDF generation failed");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="no-print"><Nav /></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 lg:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <div className="label-eyebrow">{t("dashboard.eyebrow")}</div>
            <h1 className="mt-2 font-display text-3xl lg:text-4xl font-extrabold tracking-tight">
              {t("dashboard.title")}
            </h1>
            <p className="mt-2 text-muted-foreground text-sm">
              {t("dashboard.session_id")} · <span className="font-mono text-xs">{activeSessionId.slice(0, 8)}</span>
              {sessions.length > 1 && (
                <select
                  value={activeSessionId}
                  onChange={(e) => setActiveSessionId(e.target.value)}
                  className="ml-3 text-xs border border-border rounded px-2 py-1 bg-white"
                  data-testid="dashboard-session-select"
                >
                  {sessions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {new Date(s.started_at).toLocaleDateString()}
                    </option>
                  ))}
                </select>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={printPDF} disabled={pdfBusy || !report} className="no-print inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold border border-border bg-white hover:bg-muted disabled:opacity-50 transition-colors" data-testid="dashboard-pdf-btn">
              {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {pdfBusy ? t("dashboard.generating_pdf") : t("dashboard.download_pdf")}
            </button>
            {!paid.premium_unlocked && (
              <button
                onClick={() => setShowPay(true)}
                className="no-print btn-accent inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold"
                data-testid="dashboard-upsell-btn"
              >
                <Sparkles className="w-4 h-4" /> {t("dashboard.upgrade")}
              </button>
            )}
          </div>
        </div>

        {/* BharatIQ Score */}
        <div className="mt-8"><BharatIQScoreCard scores={scores} /></div>

        {/* Top scores row */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <ScoreCard label={t("result.ct_label")} value={scores.iq_score} suffix="/100" sub={t("dashboard.percentile", { p: scores.iq_percentile })} icon={Brain} />
          <ScoreCard label={t("dashboard.eq_label")} value={scores.eq_score} suffix="/100" sub={t("dashboard.emo_intel")} icon={Trophy} />
          <ScoreCard label={t("dashboard.top_career_label")} value={scores.top_careers[0]?.title} sub={t("dashboard.fit", { f: scores.top_careers[0]?.fit_score })} icon={Target} isText />
        </div>

        {scores.derived && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="derived-metrics">
            {[
              ["leadership_potential", t("dashboard.leadership")],
              ["analytical_ability", t("dashboard.analytical")],
              ["communication_potential", t("dashboard.communication")],
            ].map(([k, label]) => (
              <div key={k} className="rounded-xl border border-border bg-white p-5" data-testid={`derived-${k}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
                  <span className="font-display text-xl font-extrabold text-primary">{Math.round(scores.derived[k] ?? 0)}<span className="text-xs text-muted-foreground font-medium">/100</span></span>
                </div>
                <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-accent" style={{ width: `${scores.derived[k] ?? 0}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Coupon Wallet */}
        {coupons.length > 0 && (
          <div className="mt-6 no-print" data-testid="coupon-wallet">
            <h2 className="font-display text-xl font-extrabold tracking-tight flex items-center gap-2">
              <Gift className="w-4 h-4 text-accent" /> Coupon Wallet
            </h2>
            <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {coupons.map((c) => <CouponCard key={c.id} coupon={c} />)}
            </div>
          </div>
        )}

        {/* Charts */}
        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          <div className="rounded-xl border border-border bg-white p-6">
            <div className="label-eyebrow">Big Five Personality</div>
            <div className="mt-3 h-[300px]" data-testid="personality-radar">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} outerRadius={100}>
                  <PolarGrid stroke="#E2E8F0" />
                  <PolarAngleAxis dataKey="trait" tick={{ fontSize: 12, fill: "#0A192F" }} />
                  <Radar name="You" dataKey="value" stroke="#0A192F" fill="#0A192F" fillOpacity={0.3} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-white p-6">
            <div className="label-eyebrow">RIASEC Career Interest</div>
            <div className="mt-3 h-[300px]" data-testid="riasec-bar">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={riasecData}>
                  <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                  <XAxis dataKey="code" tick={{ fontSize: 12, fill: "#0A192F" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                  <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Bar dataKey="value" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              {riasecData.map((r) => (<span key={r.code}><b className="text-primary">{r.code}</b>·{r.label}</span>))}
            </div>
          </div>
        </div>

        {/* AI Report */}
        <div className="mt-10">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl font-extrabold tracking-tight">AI Career Report</h2>
            {report && <span className="label-eyebrow">{report.tier === "premium" ? "Premium Blueprint" : "Basic Report"}</span>}
          </div>

          {report ? (
            <div className="mt-5 space-y-6">
              <Section title="Summary" icon={Lightbulb}>
                <p className="text-foreground/85 leading-relaxed whitespace-pre-wrap">{report.summary}</p>
              </Section>

              <div className="grid md:grid-cols-2 gap-6">
                <Section title="Strengths" icon={Trophy}>
                  <ul className="space-y-2 text-sm text-foreground/85">
                    {(report.strengths || []).map((s, i) => (
                      <li key={i} className="flex gap-2"><span className="text-primary font-bold">•</span><span>{s}</span></li>
                    ))}
                  </ul>
                </Section>
                <Section title="Growth Areas" icon={Target}>
                  <ul className="space-y-2 text-sm text-foreground/85">
                    {(report.weaknesses || []).map((s, i) => (
                      <li key={i} className="flex gap-2"><span className="text-accent font-bold">•</span><span>{s}</span></li>
                    ))}
                  </ul>
                </Section>
              </div>

              <Section title="Career Paths" icon={TrendingUp}>
                <div className="grid sm:grid-cols-2 gap-4">
                  {(report.career_paths || []).map((c, i) => (
                    <div key={i} className="rounded-lg border border-border p-4 hover:bg-muted/30 transition-colors">
                      <div className="font-display font-bold text-lg">{c.title}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{c.starting_salary_inr}</div>
                      <div className="mt-2 text-sm text-foreground/80 leading-snug">{c.why_fit}</div>
                      {c.growth_outlook && (
                        <div className="mt-2 text-xs text-primary font-medium">{c.growth_outlook}</div>
                      )}
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Next Steps" icon={ArrowRight}>
                <ol className="space-y-2 text-sm text-foreground/85 list-decimal list-inside">
                  {(report.next_steps || []).map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              </Section>

              {/* Premium sections */}
              {report.tier === "premium" && report.roadmap_6_months && (
                <Section title="6-Month Roadmap" icon={Sparkles}>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {report.roadmap_6_months.map((m, i) => (
                      <div key={i} className="rounded-lg border border-border p-4">
                        <div className="flex items-center justify-between">
                          <span className="font-display font-bold">{m.month}</span>
                          <span className="text-[10px] uppercase tracking-widest text-accent">{m.focus}</span>
                        </div>
                        <ul className="mt-3 space-y-1.5 text-xs text-foreground/80">
                          {(m.actions || []).map((a, j) => (
                            <li key={j} className="flex gap-1.5"><span className="text-primary">→</span><span>{a}</span></li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {report.tier === "premium" && report.skill_gaps && (
                <Section title="Skill Gap Analysis" icon={Target}>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {report.skill_gaps.map((s, i) => (
                      <div key={i} className="rounded-lg border border-border p-4">
                        <div className="font-display font-bold">{s.skill}</div>
                        <div className="mt-2 flex items-center gap-2 text-xs">
                          <span className="px-2 py-0.5 rounded bg-muted">{s.current_level}</span>
                          <ArrowRight className="w-3 h-3 text-muted-foreground" />
                          <span className="px-2 py-0.5 rounded bg-accent/15 text-accent font-medium">{s.target_level}</span>
                        </div>
                        <div className="mt-2 text-xs text-muted-foreground">
                          {(s.resources || []).join(" · ")}
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}
            </div>
          ) : (paid.basic_unlocked || paid.premium_unlocked) ? (
            <div className="mt-5 rounded-xl border border-border bg-white p-10 text-center">
              <Loader2 className="w-6 h-6 mx-auto text-primary animate-spin" />
              <div className="mt-3 font-display text-xl font-bold">Generating your AI report</div>
              <div className="mt-1 text-sm text-muted-foreground">Claude is reading your scores and crafting a personalized career report. This usually takes 10-20 seconds.</div>
            </div>
          ) : (
            <div className="mt-5 rounded-xl border border-border bg-white p-10 text-center">
              <Lock className="w-6 h-6 mx-auto text-primary" />
              <div className="mt-3 font-display text-xl font-bold">Report locked</div>
              <div className="mt-1 text-sm text-muted-foreground">Unlock your full report to see strengths, careers and action plan.</div>
              <button onClick={() => setShowPay(true)} className="mt-5 btn-primary px-5 py-2.5 rounded-md font-semibold inline-flex items-center gap-2" data-testid="dashboard-unlock-btn">
                Unlock for ₹49 <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* AI Advisor Chat (Premium) */}
        <div className="mt-10 no-print" id="advisor">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl font-extrabold tracking-tight inline-flex items-center gap-2">
              <MessageCircle className="w-5 h-5" /> AI Career Advisor
            </h2>
            {!paid.premium_unlocked && <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Premium only</span>}
          </div>

          <div className="mt-5 rounded-xl border border-border bg-white overflow-hidden relative" data-testid="advisor-panel">
            <div className="h-[420px] overflow-y-auto p-5 space-y-3 bg-muted/20">
              {paid.premium_unlocked ? (
                <>
                  {chat.length === 0 && (
                    <div className="text-center text-sm text-muted-foreground py-8">
                      <Sparkles className="w-5 h-5 mx-auto text-accent" />
                      <div className="mt-2">Ask anything about your career path — based on your scores.</div>
                    </div>
                  )}
                  {chat.map((m, i) => (
                    <div key={m.id || i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] rounded-lg px-4 py-2.5 text-sm leading-relaxed ${
                        m.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-white border border-border text-foreground/90"
                      }`}>
                        {m.content}
                      </div>
                    </div>
                  ))}
                  {chatSending && (
                    <div className="flex justify-start"><div className="bg-white border border-border rounded-lg px-4 py-2.5 text-sm inline-flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Thinking…</div></div>
                  )}
                  <div ref={chatBottomRef} />
                </>
              ) : (
                <div className="h-full grid place-items-center text-center">
                  <div>
                    <Lock className="w-6 h-6 mx-auto text-primary" />
                    <div className="mt-3 font-display text-lg font-bold">Unlock Career Blueprint</div>
                    <div className="mt-1 text-sm text-muted-foreground max-w-sm">Get a private AI advisor trained on your assessment scores. ₹444 one-time.</div>
                    <button onClick={() => setShowPay(true)} className="mt-4 btn-accent px-5 py-2.5 rounded-md font-semibold inline-flex items-center gap-2" data-testid="advisor-upgrade-btn">
                      <Sparkles className="w-4 h-4" /> Upgrade
                    </button>
                  </div>
                </div>
              )}
            </div>
            {paid.premium_unlocked && (
              <form onSubmit={sendChat} className="border-t border-border p-3 flex gap-2 bg-white">
                <input
                  data-testid="advisor-input"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="e.g. Should I move from engineering to PM?"
                  className="flex-1 px-3 py-2 rounded-md border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  disabled={chatSending}
                />
                <button type="submit" disabled={chatSending || !chatInput.trim()} className="btn-primary px-4 rounded-md inline-flex items-center gap-1.5 text-sm font-semibold disabled:opacity-50" data-testid="advisor-send-btn">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <PaymentModal
        open={showPay}
        onClose={() => setShowPay(false)}
        sessionId={activeSessionId}
        tier={paid.basic_unlocked ? "premium" : "basic"}
        onSuccess={() => {
          setShowPay(false);
          setActiveSessionId(activeSessionId); // trigger re-fetch
          (async () => {
            const p = await api.get(`/payments/status/${activeSessionId}`);
            setPaid(p.data);
            try {
              const r = await api.get(`/reports/${activeSessionId}`);
              setReport(r.data);
            } catch (e) {}
          })();
        }}
      />
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="rounded-xl border border-border bg-white p-6">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-primary" />
        <h3 className="font-display text-lg font-bold">{title}</h3>
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function CouponCard({ coupon }) {
  const copy = () => {
    navigator.clipboard.writeText(coupon.code);
    toast.success("Code copied");
  };
  const used = coupon.status === "used" || coupon.used_count >= coupon.max_uses;
  const expired = coupon.redeem_hint === "Coupon expired";
  const dead = used || expired;
  return (
    <div className={`relative rounded-xl border p-5 overflow-hidden ${
      dead ? "border-border bg-muted/30 opacity-70" : "border-accent/40 bg-accent/5"
    }`} data-testid={`coupon-${coupon.code}`}>
      <div className="absolute -right-8 -top-8 w-24 h-24 rounded-full bg-accent/15" />
      <div className="relative">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-widest text-accent">{coupon.title}</span>
          <span className="text-xs font-bold text-primary">{coupon.discount_percent}% OFF</span>
        </div>
        <div className="mt-3 font-mono font-extrabold text-lg text-primary tracking-wide">{coupon.code}</div>
        <div className="mt-1.5 text-xs text-muted-foreground line-clamp-2">{coupon.description}</div>
        <div className="mt-4 flex items-center justify-between text-[11px] text-muted-foreground">
          <span>
            {used ? "Used" : expired ? "Expired" : coupon.expires_at ? `Expires ${new Date(coupon.expires_at).toLocaleDateString()}` : "No expiry"}
          </span>
          {!dead && (
            <button onClick={copy} className="inline-flex items-center gap-1 text-primary font-semibold hover-underline" data-testid={`copy-${coupon.code}`}>
              <Copy className="w-3 h-3" /> Copy
            </button>
          )}
        </div>
        {!dead && (
          <div className="mt-2 flex items-center gap-3 text-[10px] text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> Sent to email</span>
            <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> WhatsApp</span>
          </div>
        )}
      </div>
    </div>
  );
}

function ScoreCard({ label, value, suffix, sub, icon: Icon, isText }) {  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-border bg-white p-6"
    >
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">{label}</div>
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className={`font-display font-extrabold text-primary ${isText ? "text-2xl" : "text-5xl"}`}>
          {value}
        </span>
        {suffix && <span className="text-sm text-muted-foreground">{suffix}</span>}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
    </motion.div>
  );
}
